import { ReactNode } from 'react';
import classNames from 'classnames';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import type { KcDelta } from '../contract';
import { ESTIMATE_TAG, NEW_TAG } from '../labels';
import { formatPct, formatSigned } from '../format';
import styles from '../styles.module.scss';

export interface DeltaCellProps {
  diff?: number | null;
  pct?: number | null;
  /** Tiện cho KcDelta / KcRowDelta: dùng thay cho diff + pct. */
  delta?: Pick<KcDelta, 'diff' | 'pct'> | null;
  /** Một phía là ESTIMATE → thẻ "ước tính". */
  estimated?: boolean;
  /** Số tháng gốc: `pct = null` mà số gốc > 0 thì hiện "mới" (mục 5.6). */
  baseValue?: number | null;
  /** both (mặc định): chênh + %; diff: chỉ chênh; pct: chỉ %. */
  show?: 'both' | 'diff' | 'pct';
  /** Dòng phụ (vd. `kc.sameDay.label`). */
  sub?: ReactNode;
  /** Định dạng số chênh (mặc định số đầy đủ có dấu: +2.881.908). */
  format?: (n: number) => string;
}

/**
 * Mũi tên + số + % tăng / giảm (mục 8.6, 8.9): tăng chữ successInk nền successSoft, giảm dangerInk nền
 * dangerSoft; luôn kèm mũi tên và dấu +/− (không chỉ dựa vào màu). Số do API tính sẵn, ô này không tự tính.
 */
const DeltaCell = ({
  diff: diffProp,
  pct: pctProp,
  delta,
  estimated,
  baseValue,
  show = 'both',
  sub,
  format = formatSigned,
}: DeltaCellProps) => {
  const diff = delta ? delta.diff : diffProp ?? null;
  const pct = delta ? delta.pct : pctProp ?? null;
  const isNew = pct === null && typeof baseValue === 'number' && baseValue > 0;

  let chip: ReactNode;
  if (isNew) {
    chip = (
      <span className={classNames(styles.deltaChip, styles.deltaUp)} aria-label="Mới có số">
        <ArrowUpOutlined aria-hidden />
        {NEW_TAG}
      </span>
    );
  } else if (diff === null && pct === null) {
    chip = <span>—</span>;
  } else {
    const sign = diff !== null ? Math.sign(diff) : Math.sign(pct ?? 0);
    const parts: string[] = [];
    if (show !== 'pct' && diff !== null) parts.push(format(diff));
    if (show !== 'diff' && pct !== null) {
      parts.push(show === 'both' && diff !== null ? `(${formatPct(pct)})` : formatPct(pct));
    }
    const text = parts.join(' ') || '—';
    chip = (
      <span
        className={classNames(styles.deltaChip, {
          [styles.deltaUp]: sign > 0,
          [styles.deltaDown]: sign < 0,
          [styles.deltaFlat]: sign === 0,
        })}
        aria-label={`${sign > 0 ? 'Tăng' : sign < 0 ? 'Giảm' : 'Không đổi'} ${text}`}
      >
        {sign > 0 && <ArrowUpOutlined aria-hidden />}
        {sign < 0 && <ArrowDownOutlined aria-hidden />}
        {sign === 0 && <span aria-hidden>=</span>}
        {text}
      </span>
    );
  }

  return (
    <span>
      <span className={styles.delta}>
        {chip}
        {estimated && <span className={styles.estimateTag}>{ESTIMATE_TAG}</span>}
      </span>
      {sub && <span className={styles.deltaSub}>{sub}</span>}
    </span>
  );
};

export default DeltaCell;
